import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    ChargeGroupInterface,
    ChargeGroupModel,
    ChargeSchedulesService,
    ChargeTypeInterface,
    ChargeTypeModel,
    HttpStatusCodesEnum,
    ReportsService,
    ServerResponse,
    TradingAccountFilterModel,
    TradingAccountReportInterface,
    TradingAccountReportModel,
    TradingGroupInterface,
    TradingGroupModel,
    TradingGroupSearchModel,
    TradingPlatformInfoInterface,
    TradingPlatformService,
    TradingService
} from '@spa/api';

import {FilterControl, Helper} from 'appShared/classes';
import {Pagination, ReportTableConfigInterface} from 'appShared/components';

import {AccountsReportResolverDataInterface} from '../interfaces';
import {variables} from 'appAssets/tenants/variables';
import {ComponentStorageKeyProvider} from 'appShared/providers';
import {CacheCollectionService} from 'appShared/services';
import {switchMap} from 'rxjs/internal/operators';
import {PanelAccountsEditFeePlansResolverErrorStatesEnum, TradingGroupClientTypesEnum} from 'appEntries/panel/enums';
import {PanelAccountsWithChargeSchedulesDataTableConfig} from 'appAssets/descriptors';

export abstract class AccountsReportResolver
    implements Resolve<ApiResponse<ApiError[] | AccountsReportResolverDataInterface>> {
    protected componentStorageKeyProvider: ComponentStorageKeyProvider;
    protected constructor(
        protected tradingService: TradingService,
        protected chargeSchedulesService: ChargeSchedulesService,
        protected tradingPlatformService: TradingPlatformService,
        protected reportsService: ReportsService,
        protected cacheCollectionService: CacheCollectionService,
        protected storageId: string) {
        this.componentStorageKeyProvider = new ComponentStorageKeyProvider(storageId);
    }

    private mapData(data: TradingAccountReportInterface[]): TradingAccountReportModel[] {
        return data.map((account: TradingAccountReportInterface) => {
            const model = new TradingAccountReportModel(account);

            model.accountTypeName = Helper.textsHelper().getTradingAccountTypeLabel(model.isCashAccount, model.accountSubType);

            if (!model.isCashAccount) {
                model.accountStatusName = Helper.textsHelper().getTradingAccountStatusName(model.accountStatus);
            } else {
                model.accountStatusName = Helper.textsHelper().getCashAccountStatusName(model.accountStatus);
            }

            return model;
        });
    }

    private getAllChargeTypes(allFeeGroups: ChargeGroupModel[]): ServerResponse<AccountsReportResolverDataInterface> {
        return this.chargeSchedulesService.getAllChargeTypes().pipe(
            switchMap(feeTypesResponse => {
                if (feeTypesResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<ChargeTypeInterface[]>(
                        feeTypesResponse,
                        PanelAccountsEditFeePlansResolverErrorStatesEnum.GetFeeTypesFailed
                    );
                }

                const allFeeTypes = Helper.collectionFactory<ChargeTypeInterface, ChargeTypeModel>(
                    ChargeTypeModel,
                    feeTypesResponse.data as ChargeTypeInterface[]
                );

                allFeeGroups.forEach((chargeGroup: ChargeGroupModel) => {
                    chargeGroup.chargeTypes = allFeeTypes.filter(chargeType => chargeType.chargeGroupCode === chargeGroup.name);
                });

                return this.getTradingPlatforms(allFeeGroups);
            })
        );
    }
    protected abstract getDefaultFilterModel(): TradingAccountFilterModel;

    protected abstract getFilterControls(
        tradingPlatforms: TradingPlatformInfoInterface[],
        allFeeGroups: ChargeGroupModel[],
        allAccountGroups: TradingGroupModel[]): FilterControl[];

    protected abstract getDataTableConfig(): ReportTableConfigInterface;

    public getAccountGroups(tradingPlatforms: TradingPlatformInfoInterface[],
                            allFeeGroups: ChargeGroupModel[]): ServerResponse<AccountsReportResolverDataInterface>{
        const search = new TradingGroupSearchModel(
            null,
            null,
            null,
            null,
            null,
            Helper.enumsHelper().getTradingGroupBehaviorTypes(TradingGroupClientTypesEnum.All),
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            Pagination.defaultPagesChunk,
            Pagination.calculateItemsPerPagesChunk(),
            'id',
            false
        );

        return this.tradingService.searchGroup(search).pipe(
            switchMap(response => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable(response);
                }

                const groups = response.data as TradingGroupInterface[];
                const groupModels = groups.map(g => new TradingGroupModel(g));
                return this.getReportData(tradingPlatforms, allFeeGroups, groupModels);
            }),
        );
    }

    public getTradingPlatforms(allFeeGroups: ChargeGroupModel[]): ServerResponse<AccountsReportResolverDataInterface>{
        return this.tradingPlatformService.getAll(variables.tenant).pipe(
            switchMap(response => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                     return Helper.errorsHelper().createErrorResponseObservable(response);
                }

                const tradingPlatforms = response.data as TradingPlatformInfoInterface[];

                return this.getAccountGroups(tradingPlatforms, allFeeGroups);
            }),
        );
    }

    public getFeeGroups(): ServerResponse<AccountsReportResolverDataInterface>{
        return this.chargeSchedulesService.getAllChargeGroups()
            .pipe(
                switchMap(feeGroupsResponse => {
                    if (feeGroupsResponse.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<ChargeGroupInterface[]>(
                            feeGroupsResponse,
                            PanelAccountsEditFeePlansResolverErrorStatesEnum.GetFeeGroupsFailed
                        );
                    }

                    const allFeeGroups = Helper.collectionFactory<ChargeGroupInterface, ChargeGroupModel>(
                        ChargeGroupModel,
                        feeGroupsResponse.data as ChargeGroupInterface[]
                    );

                    return this.getAllChargeTypes(allFeeGroups);
                })
            );
    }

    public getReportData(
        tradingPlatforms: TradingPlatformInfoInterface[],
        allFeeGroups: ChargeGroupModel[],
        allAccountGroups: TradingGroupModel[]): ServerResponse<AccountsReportResolverDataInterface>{
        const filterControls = this.getFilterControls(tradingPlatforms, allFeeGroups, allAccountGroups);
        const filterCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data;
        const filter = filterCache ?? this.getDefaultFilterModel();

        return this.reportsService.getTradingAccountsReport(filter)
            .pipe(
                map((response: ApiResponse<ApiError[] | TradingAccountReportInterface[]>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(response);
                    }

                    const data = response.data;

                    let modelsCollection = [];

                    if (data !== null) {
                        modelsCollection = this.mapData(data as TradingAccountReportInterface[]);
                    }

                    return new ApiResponse<AccountsReportResolverDataInterface>(HttpStatusCodesEnum.Ok, {
                        filterControls,
                        dataTableConfig: this.getDataTableConfig(),
                        dataTableWithFeesConfig: PanelAccountsWithChargeSchedulesDataTableConfig(),
                        modelsCollection,
                        filterModel: filter,
                        tradingPlatforms,
                        allFeeGroups
                    });
                })
            );
    }

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<ApiResponse<ApiError[] | AccountsReportResolverDataInterface>> {
        return this.getFeeGroups();
    }
}
